import { Link } from "react-router-dom";
import { useState } from "react";
import { Mail, Phone, MapPin, Instagram, Facebook, Linkedin, Youtube, Leaf, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import axios from "axios";
import { API } from "@/App";

const quickLinks = [
  { name: "Home", path: "/" },
  { name: "Products", path: "/products" },
  { name: "About Us", path: "/about" },
  { name: "Blog", path: "/blog" },
];

const supportLinks = [
  { name: "FAQ", path: "/faq" },
  { name: "Contact", path: "/contact" },
  { name: "Testimonials", path: "/testimonials" },
  { name: "Spotlight", path: "/spotlight" },
];

const learnLinks = [
  { name: "About Microgreens", path: "/about-microgreens" },
  { name: "E-Book Guide", path: "/ebook" },
  { name: "Health Benefits", path: "/about-microgreens#benefits" },
];

export default function Footer() {
  const [email, setEmail] = useState("");
  const [subscribing, setSubscribing] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;

    setSubscribing(true);
    try {
      await axios.post(`${API}/newsletter`, { email });
      toast.success("Successfully subscribed to our newsletter!");
      setEmail("");
    } catch (error) {
      toast.error("Failed to subscribe. Please try again.");
    } finally {
      setSubscribing(false);
    }
  };

  return (
    <footer className="bg-praroha-forest text-white mt-20 rounded-t-3xl" data-testid="main-footer">
      {/* Newsletter Section */}
      <div className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="font-heading text-2xl md:text-3xl font-semibold mb-3">
                Join the Praroha Greens Community
              </h3>
              <p className="text-white/70 text-sm md:text-base">
                Get exclusive recipes, health tips, and special offers delivered to your inbox.
              </p>
            </div>
            <form onSubmit={handleSubscribe} className="flex gap-3" data-testid="newsletter-form">
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1 bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:border-white/40"
                data-testid="newsletter-email-input"
              />
              <Button
                type="submit"
                disabled={subscribing}
                className="bg-praroha-terracotta hover:bg-praroha-terracotta/90 text-white rounded-full px-6"
                data-testid="newsletter-submit-btn"
              >
                {subscribing ? "..." : <ArrowRight className="w-5 h-5" />}
              </Button>
            </form>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-8">
          {/* Brand */}
          <div className="col-span-2 lg:col-span-1">
            <Link to="/" className="flex items-center gap-2 mb-4">
              <img 
                src="/praroha-logo-footer.png" 
                alt="Praroha Greens Logo"
                className="h-16 w-auto"
              />
            </Link>
            <p className="text-white/70 text-sm mb-4">
              Fresh, nutrient-dense microgreens delivered from our farm to your kitchen.
            </p>
            <div className="flex gap-3">
              <a
                href="https://www.instagram.com/prarohagreens/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors"
                data-testid="social-instagram"
              >
                <Instagram className="w-4 h-4" />
              </a>
              <a
                href="https://www.facebook.com/p/Praroha-Greens-61573828271977/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors"
                data-testid="social-facebook"
              >
                <Facebook className="w-4 h-4" />
              </a>
              <a
                href="https://in.linkedin.com/company/praroha-greens"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors"
                data-testid="social-linkedin"
              >
                <Linkedin className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-heading text-lg font-semibold mb-4">Quick Links</h4>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-white/70 text-sm hover:text-white transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Support */}
          <div>
            <h4 className="font-heading text-lg font-semibold mb-4">Support</h4>
            <ul className="space-y-2">
              {supportLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-white/70 text-sm hover:text-white transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Learn */}
          <div>
            <h4 className="font-heading text-lg font-semibold mb-4">Learn</h4>
            <ul className="space-y-2">
              {learnLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-white/70 text-sm hover:text-white transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-heading text-lg font-semibold mb-4">Contact Us</h4>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 mt-1 text-praroha-terracotta flex-shrink-0" />
                <span className="text-white/70 text-sm">
                  Landmark Residency Madeenaguda, Miyapur<br />Hyderabad - 500049 Telangana
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-4 h-4 text-praroha-terracotta flex-shrink-0" />
                <a href="tel:+919640300086" className="text-white/70 text-sm hover:text-white transition-colors">
                  +91 &nbsp;96403 00086
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-praroha-terracotta flex-shrink-0" />
                <a href="mailto:info@prarohagreens.com" className="text-white/70 text-sm hover:text-white transition-colors break-all">
                  info@prarohagreens.com
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* FSSAI Badge */}
          <div className="flex items-center justify-center gap-2 sm:gap-3 mb-5 pb-5 border-b border-white/10">
            <div className="flex flex-col sm:flex-row items-center gap-1 sm:gap-3 px-3 sm:px-4 py-2 bg-white/5 rounded-lg" data-testid="fssai-badge">
              <span className="text-white/90 text-[10px] sm:text-xs font-semibold tracking-wide uppercase">FSSAI Regd No.</span>
              <span className="text-white font-heading text-xs sm:text-sm font-bold tracking-wider">23625033000933</span>
            </div>
          </div>
          <div className="flex flex-col md:flex-row justify-between items-center gap-3 sm:gap-4">
            <p className="text-white/50 text-xs sm:text-sm text-center md:text-left">
              © {new Date().getFullYear()} Praroha Greens. Website sowed and harvested freshly by{" "}
              <a href="https://kensworth.in" target="_blank" rel="noopener noreferrer" className="text-white/70 hover:text-white transition-colors underline underline-offset-2">kensworth.in</a>
            </p>
            <div className="flex gap-4 sm:gap-6">
              <Link to="/privacy" className="text-white/50 text-xs sm:text-sm hover:text-white transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-white/50 text-xs sm:text-sm hover:text-white transition-colors">
                Terms & Conditions
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
